<template>
  <div class="main container-fluid wp-deploy-page">
    <header class="wpd-hero d-flex align-items-start justify-content-between gap-3">
      <div>
        <h4 class="wpd-hero__title mb-1">Деплой WordPress</h4>
        <small class="wpd-hero__subtitle">Список деплойментов, форма создания и перезапуск на основе существующего деплоймента.</small>
      </div>
      <div class="d-flex gap-2 wpd-hero__actions">
        <button v-if="isListMode" class="btn btn-light btn-sm wpd-hero__action-main" @click="openCreateMode">Создать деплоймент</button>
        <button v-else class="btn btn-outline-light btn-sm" @click="openListMode">К списку</button>
      </div>
    </header>

    <div class="tool-notifications" aria-live="polite" aria-atomic="true">
      <transition-group name="tool-toast" tag="div" class="tool-notifications__list">
        <div v-if="uiError" key="tool-error" class="tool-notification tool-notification--error">
          {{ uiError }}
        </div>
        <div v-if="infoMessage" key="tool-info" class="tool-notification tool-notification--info">
          {{ infoMessage }}
        </div>
      </transition-group>
    </div>

    <div v-if="isListMode" class="px-3 pb-2">
      <div class="card shadow-sm border-0 section-card wpd-surface">
        <div class="card-body py-2">
          <div class="d-flex align-items-center justify-content-between gap-2 history-headline">
            <h6 class="section-title mb-0">Ваши деплойменты</h6>
            <div class="d-flex align-items-center gap-2">
              <small v-if="historyTotalItems > 0" class="text-muted">Всего: {{ historyTotalItems }}</small>
              <button class="btn btn-light border btn-sm py-0 px-2" :disabled="historyLoading" @click="loadHistory()">Обновить</button>
            </div>
          </div>
          <div v-if="historyLoading && !historyItems.length" class="small text-muted mt-2">Загрузка...</div>
          <div v-else-if="historyError" class="alert alert-danger py-2 px-3 mt-2 mb-0">{{ historyError }}</div>
          <div v-else-if="!historyItems.length" class="small text-muted mt-2">Пока нет деплойментов</div>
          <div v-else class="history-list mt-2">
            <div v-for="item in historyItems" :key="item.deploymentId" class="history-item" :class="{ active: isCurrentDeployment(item.deploymentId) }">
              <div class="history-main">
                <div class="mono" :title="item.deploymentId">{{ item.deploymentId }}</div>
                <small class="text-muted">{{ formatDate(item.createdAt) }}</small>
                <a
                  v-if="item.siteUrl"
                  class="history-site"
                  :href="item.siteUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ item.siteUrl }}
                </a>
              </div>
              <div class="history-actions">
                <span class="badge rounded-pill" :class="statusClass(item.status)">{{ upper(item.status || 'UNKNOWN') }}</span>
                <button class="btn btn-light border btn-sm" :disabled="isDeletingDeployment(item.deploymentId)" @click="openDeployment(item.deploymentId)">
                  {{ isCurrentDeployment(item.deploymentId) ? 'Открыт' : 'Открыть' }}
                </button>
                <button
                  class="btn btn-outline-danger btn-sm"
                  :disabled="!canDeleteDeployment(item.status) || isDeletingDeployment(item.deploymentId)"
                  :title="!canDeleteDeployment(item.status) ? 'Удаление доступно только для SUCCESS/FAILED' : ''"
                  @click="requestDeleteDeployment(item)"
                >
                  <span v-if="isDeletingDeployment(item.deploymentId)" class="spinner-border spinner-border-sm me-1"></span>
                  Удалить
                </button>
              </div>
            </div>
          </div>
          <div v-if="historyTotalPages > 0" class="history-pagination-wrap mt-2">
            <small class="text-muted">На странице: {{ historySize }}</small>
            <nav v-if="historyTotalPages > 1">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: historyPage <= 1 }" @click="goToHistoryPage(historyPage - 1)">
                  <a class="page-link">&laquo;</a>
                </li>
                <li
                  v-for="page in historyPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: page === historyPage }"
                  @click="goToHistoryPage(page)"
                >
                  <a class="page-link">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: historyPage >= historyTotalPages }" @click="goToHistoryPage(historyPage + 1)">
                  <a class="page-link">&raquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isFormMode" class="px-3 pb-3">
      <div class="card shadow-sm border-0 section-card wpd-surface">
        <div class="card-body">
          <h6 class="section-title mb-3">{{ isDetailsMode ? 'Параметры деплоймента (перезапуск)' : 'Создать деплоймент' }}</h6>
          <form @submit.prevent="submitDeployment">
            <div class="form-check deployment-credential-check mb-3">
              <input
                id="upsert-domain-credential"
                v-model="createOrUpdateCredential"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="upsert-domain-credential">
                создать/обновить credential в системе для этого домена
              </label>
            </div>
            <div class="form-layout">
              <div class="section-block section-span-2">
                <div class="section-head">Проект-концепция</div>
                <div class="wpd-concept-picker">
                  <div class="row g-2 align-items-end">
                    <div class="col-lg-8">
                      <label class="form-label">Выберите проект из инструмента концепций</label>
                      <select v-model="selectedConceptProjectId" class="form-select form-select-sm" :disabled="conceptProjectsLoading || applyingConceptProject">
                        <option value="">Выберите проект</option>
                        <option v-for="item in conceptProjects" :key="item.id" :value="item.id">
                          {{ item.topicName || item.id }} · {{ item.selectedDomainName || 'домен не выбран' }}
                        </option>
                      </select>
                    </div>
                    <div class="col-lg-4 d-flex gap-2">
                      <button class="btn btn-primary btn-sm" type="button" :disabled="!selectedConceptProjectId || applyingConceptProject" @click="applyConceptToForm">
                        <span v-if="applyingConceptProject" class="spinner-border spinner-border-sm me-1"></span>
                        Применить
                      </button>
                    </div>
                  </div>
                  <small v-if="selectedConceptProjectSummary" class="text-muted">
                    ID: {{ selectedConceptProjectSummary.id }} · Язык: {{ selectedConceptProjectSummary.languageCode || selectedConceptProjectSummary.sourceLocale || 'ru' }}
                  </small>
                  <div v-if="conceptProjectsError" class="invalid-feedback d-block">{{ conceptProjectsError }}</div>
                </div>
              </div>

              <div class="section-block section-span-2">
                <div class="section-head">Сайт</div>
                <div class="row g-2">
                  <WpFieldInput v-model="form.domain" label="Домен *" type="text" wrapper-class="col-md-6" :error="fieldErrors.domain" />
                  <WpFieldInput v-model="form.sitePath" label="Путь сайта от корня SSH *" type="text" wrapper-class="col-md-6" :error="fieldErrors.sitePath" />
                </div>
              </div>

              <div class="section-block section-span-2">
                <div class="section-head">Title и Description главной страницы</div>
                <div class="row g-2">
                  <WpFieldInput v-model="form.siteTitle" label="Название сайта *" type="text" wrapper-class="col-md-6" :error="fieldErrors.siteTitle" />
                  <WpFieldInput v-model="form.blogDescription" label="Слоган *" type="text" wrapper-class="col-md-6" :error="fieldErrors.blogDescription" />
                  <WpFieldInput v-model="form.siteDescription" label="Description главной страницы *" type="text" wrapper-class="col-12" :error="fieldErrors.siteDescription" />
                </div>
              </div>

              <div class="section-block section-span-2">
                <div class="section-head">Категории</div>
                <div class="wpc-identity-categories">
                  <div class="wpc-identity-categories__head">
                    <h6 class="mb-0">Список категорий</h6>
                  </div>
                  <div v-if="!form.categories.length" class="wpc-empty-state mt-2">
                    <i class="bi bi-tags"></i>
                    <p class="mb-0">Категории не добавлены</p>
                  </div>
                  <div v-else class="wpc-identity-categories__list">
                    <article v-for="(category, index) in form.categories" :key="`category-${index}`" class="wpc-identity-category">
                      <template v-if="isEditingCategory(index)">
                        <div class="wpc-edit-grid w-100">
                          <label class="wpc-edit-field">
                            <span>Name</span>
                            <input v-model="categoryEditDraft.name" type="text" class="form-control form-control-sm" />
                          </label>
                          <label class="wpc-edit-field">
                            <span>Slug</span>
                            <input v-model="categoryEditDraft.slug" type="text" class="form-control form-control-sm" />
                          </label>
                          <label class="wpc-edit-field wpc-edit-field--full">
                            <span>Description</span>
                            <textarea v-model="categoryEditDraft.description" rows="3" class="form-control form-control-sm"></textarea>
                          </label>
                          <div class="wpc-concept-card__edit-actions">
                            <button class="btn btn-outline-secondary btn-sm" @click="cancelEditCategory">Отмена</button>
                            <button class="btn btn-primary btn-sm" @click="saveCategoryEdits(index)">Сохранить</button>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="wpc-identity-category__main">
                          <strong>{{ category.name || '—' }}</strong>
                          <span class="wpc-identity-category__slug">{{ category.slug || '—' }}</span>
                          <p class="mb-0">{{ category.description || '—' }}</p>
                        </div>
                        <div class="wpc-identity-category__actions">
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm wpc-edit-btn"
                            @click="startEditCategory(index)"
                            aria-label="Редактировать категорию"
                          >
                            <i class="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-outline-danger btn-sm wpc-delete-btn"
                            @click="removeCategoryRow(index)"
                            aria-label="Удалить категорию"
                          >
                            <i class="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </template>
                    </article>
                  </div>
                  <div v-if="isAddingCategory" class="wpc-identity-categories__form">
                    <label class="wpc-edit-field">
                      <span>Name</span>
                      <input v-model="categoryDraft.name" type="text" class="form-control form-control-sm" />
                    </label>
                    <label class="wpc-edit-field">
                      <span>Slug</span>
                      <input v-model="categoryDraft.slug" type="text" class="form-control form-control-sm" />
                    </label>
                    <label class="wpc-edit-field wpc-edit-field--full">
                      <span>Description</span>
                      <textarea v-model="categoryDraft.description" rows="3" class="form-control form-control-sm"></textarea>
                    </label>
                    <div class="wpc-concept-card__edit-actions">
                      <button class="btn btn-outline-secondary btn-sm" @click="cancelAddCategory">Отмена</button>
                      <button class="btn btn-outline-primary btn-sm" @click="addCategoryRow">Добавить категорию</button>
                    </div>
                  </div>
                  <div class="wpc-identity-categories__actions">
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="isAddingCategory ? cancelAddCategory() : startAddCategory()">
                      {{ isAddingCategory ? 'Отмена' : 'Добавить категорию' }}
                    </button>
                  </div>
                </div>
                <div v-if="fieldErrors.categories" class="invalid-feedback d-block">{{ fieldErrors.categories }}</div>
              </div>

              <div class="section-block">
                <div class="section-head">Файлы</div>
                <div class="wpd-file-panel">
                  <div class="wpd-file-inline" :class="{ 'wpd-file-inline--manual': !isProjectFileMode }">
                    <div class="wpd-file-inline__content">
                      <div v-if="isProjectFileMode" class="wpd-concept-assets">
                        <div class="wpd-concept-assets__item">
                          <span class="wpd-concept-assets__label">Logo</span>
                          <img v-if="conceptAssetPreview.logoUrl" :src="conceptAssetPreview.logoUrl" alt="Logo из концепции" class="wpd-concept-assets__thumb wpd-concept-assets__thumb--logo" />
                          <span v-else class="text-muted small">Не найден</span>
                        </div>
                        <div class="wpd-concept-assets__item">
                          <span class="wpd-concept-assets__label">Favicon</span>
                          <img v-if="conceptAssetPreview.faviconUrl" :src="conceptAssetPreview.faviconUrl" alt="Favicon из концепции" class="wpd-concept-assets__thumb wpd-concept-assets__thumb--favicon" />
                          <span v-else class="text-muted small">Не найден</span>
                        </div>
                      </div>

                      <div v-else class="row g-2 flex-nowrap wpd-manual-assets">
                        <div class="col-md-6">
                          <label class="form-label wpd-manual-file-label">Logo *</label>
                          <input
                            ref="manualLogoInputRef"
                            type="file"
                            accept=".png,.svg,.jpg,.jpeg,image/*"
                            class="d-none"
                            @change="onFileChanged($event, 'logo')"
                          />
                          <button type="button" class="btn btn-light border btn-sm wpd-manual-file-action" @click="openManualFilePicker('logo')">Выбрать logo</button>
                          <div v-if="fieldErrors.logo" class="invalid-feedback d-block">{{ fieldErrors.logo }}</div>
                          <small class="text-muted d-block mt-1">{{ fileState.logoName || 'Файл не выбран' }}</small>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label wpd-manual-file-label">Favicon *</label>
                          <input
                            ref="manualFaviconInputRef"
                            type="file"
                            accept=".ico,.png,.svg,.jpg,.jpeg,image/*"
                            class="d-none"
                            @change="onFileChanged($event, 'favicon')"
                          />
                          <button type="button" class="btn btn-light border btn-sm wpd-manual-file-action" @click="openManualFilePicker('favicon')">Выбрать favicon</button>
                          <div v-if="fieldErrors.favicon" class="invalid-feedback d-block">{{ fieldErrors.favicon }}</div>
                          <small class="text-muted d-block mt-1">{{ fileState.faviconName || 'Файл не выбран' }}</small>
                        </div>
                      </div>
                    </div>

                    <div class="btn-group btn-group-sm wpd-file-mode__switch" role="group" aria-label="Источник файлов">
                      <button
                        type="button"
                        class="btn btn-outline-primary"
                        :class="{ active: isProjectFileMode }"
                        :disabled="!hasProjectAssetPreview"
                        @click="switchFileSourceMode('project')"
                      >
                        Из проекта
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-primary"
                        :class="{ active: !isProjectFileMode }"
                        @click="switchFileSourceMode('manual')"
                      >
                        Загрузить
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="section-block">
                <div class="section-head">Администратор WordPress</div>
                <div class="row g-2">
                  <WpFieldInput v-for="field in adminFields" :key="field.key" v-model="form[field.key]" :label="field.label" :type="field.type" :wrapper-class="field.wrapperClass" :error="fieldErrors[field.key]" />
                </div>
              </div>

              <div class="section-block">
                <div class="section-head">SSH</div>
                <div class="row g-2">
                  <WpFieldInput v-model="form.sshHost" label="Хост SSH *" wrapper-class="col-lg-6 col-md-6" :error="fieldErrors.sshHost" />
                  <WpFieldInput v-model="form.sshUsername" label="Пользователь SSH *" wrapper-class="col-lg-6 col-md-6" :error="fieldErrors.sshUsername" />

                  <div class="col-12">
                    <div class="wpd-ssh-auth-switch d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <span class="form-label mb-0">Способ аутентификации</span>
                      <div class="btn-group btn-group-sm" role="group" aria-label="Способ аутентификации SSH">
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          :class="{ active: sshAuthMode === 'password' }"
                          @click="setSshAuthMode('password')"
                        >
                          Пароль
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          :class="{ active: sshAuthMode === 'key' }"
                          @click="setSshAuthMode('key')"
                        >
                          Приватный ключ
                        </button>
                      </div>
                    </div>
                  </div>

                  <WpFieldInput
                    v-if="sshAuthMode === 'password'"
                    v-model="form.sshPassword"
                    label="Пароль SSH *"
                    type="password"
                    wrapper-class="col-12"
                    :error="fieldErrors.sshPassword"
                  />
                  <template v-else>
                    <WpFieldInput
                      v-model="form.sshPrivateKey"
                      label="Приватный ключ SSH *"
                      :is-textarea="true"
                      :rows="6"
                      wrapper-class="col-12 wpd-ssh-key-field"
                      placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                      :error="fieldErrors.sshPrivateKey"
                    />
                    <div class="col-12">
                      <div class="form-check deployment-credential-check mb-0">
                        <input
                          id="ssh-use-passphrase"
                          v-model="useSshPassphrase"
                          class="form-check-input"
                          type="checkbox"
                        />
                        <label class="form-check-label" for="ssh-use-passphrase">
                          ключ защищён passphrase
                        </label>
                      </div>
                    </div>
                    <WpFieldInput
                      v-if="useSshPassphrase"
                      v-model="form.sshPrivateKeyPassphrase"
                      label="Passphrase приватного ключа"
                      type="password"
                      wrapper-class="col-12"
                      :error="fieldErrors.sshPrivateKeyPassphrase"
                    />
                  </template>

                  <div class="col-12">
                    <div class="form-check deployment-credential-check mb-0">
                      <input
                        id="ssh-use-port"
                        v-model="useSshPort"
                        class="form-check-input"
                        type="checkbox"
                      />
                      <label class="form-check-label" for="ssh-use-port">
                        указать порт SSH (по умолчанию 22)
                      </label>
                    </div>
                  </div>
                  <WpFieldInput
                    v-if="useSshPort"
                    v-model="form.sshPort"
                    label="Порт SSH"
                    type="number"
                    wrapper-class="col-md-4"
                    :error="fieldErrors.sshPort"
                  />
                </div>
              </div>

              <div class="section-block">
                <div class="section-head">Дополнительно</div>
                <div class="row g-2">
                  <WpFieldInput v-for="field in optionalFields" :key="field.key" v-model="form[field.key]" :label="field.label" :type="field.type" :wrapper-class="field.wrapperClass" :error="fieldErrors[field.key]" />
                  <div class="col-md-6">
                    <label class="form-label">Локаль</label>
                    <select v-model="form.locale" class="form-select form-select-sm">
                      <option v-for="option in localeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="section-block section-span-2">
                <div class="section-head">База данных</div>
                <div class="row g-2">
                  <WpFieldInput v-for="field in dbFields" :key="field.key" v-model="form[field.key]" :label="field.label" :type="field.type" :wrapper-class="field.wrapperClass" :error="fieldErrors[field.key]" />
                </div>
              </div>

              <div class="section-block section-span-2">
                <div class="section-head">Cloudflare Turnstile (защита формы связи)</div>
                <div class="row g-2">
                  <div class="col-12">
                    <div class="form-check deployment-credential-check mb-0">
                      <input
                        id="enable-turnstile"
                        v-model="form.useTurnstile"
                        class="form-check-input"
                        type="checkbox"
                      />
                      <label class="form-check-label" for="enable-turnstile">
                        включить Cloudflare Turnstile
                      </label>
                    </div>
                  </div>
                  <template v-if="form.useTurnstile">
                    <WpFieldInput
                      v-model="form.turnstileSiteKey"
                      label="Turnstile site key *"
                      type="text"
                      wrapper-class="col-md-6"
                      :error="fieldErrors.turnstileSiteKey"
                    />
                    <WpFieldInput
                      v-model="form.turnstileSecretKey"
                      label="Turnstile secret key *"
                      type="text"
                      wrapper-class="col-md-6"
                      :error="fieldErrors.turnstileSecretKey"
                    />
                  </template>
                </div>
              </div>

            </div>

            <div class="d-flex align-items-center justify-content-end gap-2 mt-3">
              <button class="btn btn-primary btn-sm" :disabled="submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ isDetailsMode ? 'Перезапустить деплоймент (новый id)' : 'Запустить деплоймент' }}
              </button>
            </div>
            <div v-if="submitError" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ submitError }}</div>
          </form>
        </div>
      </div>
    </div>

    <div v-if="deploymentId" class="px-3 pb-4">
      <div class="card shadow-sm border-0 section-card wpd-surface">
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div class="d-flex align-items-center gap-2">
              <small class="mono">ID деплоймента: {{ deploymentId }}</small>
              <div v-if="isRunningDeployment" class="status-progress">
                <div class="progress status-progress__bar" role="progressbar" :aria-valuenow="progressValue" aria-valuemin="0" aria-valuemax="100">
                  <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: `${progressValue}%` }"></div>
                </div>
                <small class="status-progress__label">{{ progressValue }}%</small>
              </div>
              <span v-else class="badge rounded-pill" :class="statusClass(statusValue)">{{ upper(statusValue || 'UNKNOWN') }}</span>
            </div>
            <small v-if="sseError" class="text-danger">{{ sseError }}</small>
          </div>
          <div v-if="detailsErrorMessage" class="alert alert-danger py-2 px-3 mb-2">{{ detailsErrorMessage }}</div>
          <WpDeploymentLogConsole :entries="filteredLogs" :filter="logFilter" @update:filter="logFilter = $event" @copy="copyLogs" @download="downloadLogs" @clear="clearLogs" />
        </div>
      </div>
    </div>

    <div v-if="showDeleteDeploymentModal" class="wpd-modal-backdrop" @click.self="closeDeleteDeploymentModal">
      <div class="wpd-modal">
        <div class="wpd-modal__head">
          <h6 class="mb-0">Удалить деплоймент</h6>
        </div>
        <div class="wpd-modal__body">
          <p class="mb-0">
            Деплоймент <strong>{{ deploymentDeleteCandidate?.id || '—' }}</strong> будет удален без возможности восстановления.
          </p>
        </div>
        <div class="wpd-modal__actions">
          <button class="btn btn-outline-secondary btn-sm" :disabled="Boolean(deletingDeploymentId)" @click="closeDeleteDeploymentModal">
            Отмена
          </button>
          <button class="btn btn-danger btn-sm" :disabled="Boolean(deletingDeploymentId)" @click="confirmDeleteDeployment">
            <span v-if="Boolean(deletingDeploymentId)" class="spinner-border spinner-border-sm me-1"></span>
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import WpFieldInput from '@/components/tools/WpFieldInput.vue';
import WpDeploymentLogConsole from '@/components/tools/WpDeploymentLogConsole.vue';
import { buildCreateDeploymentFormData } from '@/utils/wpDeploymentFormData';
import { useDeploymentEvents } from '@/composables/useDeploymentEvents';
import { upsertWordpressCredentialByLabel } from '@/services/credentialService';
import { createDeployment, deleteDeployment as deleteWpDeployment, getDeploymentApplicationPassword, getDeploymentLogs, getDeploymentStatus, getDeploymentsHistory, isRunningStatus, isTerminalStatus } from '@/services/wpDeploymentService';
import { getSiteProject, getSiteProjectDesign, getSiteProjectFavicon, getSiteProjectLogo, getSiteProjectResolvedContent, getSiteProjects } from '@/services/wordpressConceptService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGIN_SLUG_RE = /^[a-zA-Z0-9_-]+$/;
const MAX_LOG_LINES = 2000;
const CREATE_MODE_QUERY = 'create';
const INFO_MESSAGE_TIMEOUT_MS = 4000;
const CREDENTIAL_SYNC_INFO_TIMEOUT_MS = 6500;
const HISTORY_DEFAULT_PAGE_SIZE = 20;
const HISTORY_ROW_APPROX_HEIGHT = 72;
const HISTORY_SCREEN_RESERVED_HEIGHT = 360;
const APPLICATION_PASSWORD_RETRY_ATTEMPTS = 6;
const APPLICATION_PASSWORD_RETRY_DELAY_MS = 1500;
const createEmptyCategory = () => ({ name: '', slug: '', description: '' });
const FORM_DEFAULTS = {
  domain: '',
  sitePath: '',
  siteTitle: '',
  blogDescription: '',
  siteDescription: '',
  categories: [],
  sshHost: '',
  sshPort: 22,
  sshUsername: '',
  sshPassword: '',
  sshPrivateKey: '',
  sshPrivateKeyPassphrase: '',
  adminUser: 'admin',
  adminEmail: '',
  adminPassword: '',
  dbHost: 'localhost',
  dbName: '',
  dbUser: '',
  dbPassword: '',
  useTurnstile: false,
  turnstileSiteKey: '',
  turnstileSecretKey: '',
  loginSlug: 'streammedia',
  locale: 'ru_RU',
  permalinkStructure: '/%postname%/',
  design: null,
};
const EMPTY_CONCEPT_ASSETS = {
  logoUrl: '',
  faviconUrl: '',
};
const FILE_SOURCE_PROJECT = 'project';
const FILE_SOURCE_MANUAL = 'manual';
const REQUIRED_FIELDS = ['domain', 'sshHost', 'sshUsername', 'sitePath', 'siteTitle', 'blogDescription', 'siteDescription', 'adminUser', 'adminEmail', 'adminPassword', 'dbHost', 'dbName', 'dbUser', 'dbPassword'];
const localeOptions = [
  { value: 'ru_RU', label: 'Русский (ru_RU)' },
  { value: 'en_US', label: 'Английский (en_US)' },
  { value: 'es_ES', label: 'Испанский (es_ES)' },
];

const route = useRoute();
const router = useRouter();
const submitting = ref(false);
const submitError = ref('');
const uiError = ref('');
const infoMessage = ref('');
const deploymentId = ref('');
const deploymentStatus = ref(null);
const logFilter = ref('all');
const logs = ref([]);
const historyItems = ref([]);
const historyLoading = ref(false);
const historyError = ref('');
const historyPage = ref(1);
const historySize = ref(HISTORY_DEFAULT_PAGE_SIZE);
const historyTotalItems = ref(0);
const historyTotalPages = ref(0);
const deletingDeploymentId = ref('');
const deploymentDeleteCandidate = ref(null);
const createOrUpdateCredential = ref(true);
const sshAuthMode = ref('password');
const useSshPassphrase = ref(false);
const useSshPort = ref(false);
const fieldErrors = reactive({});
const fileState = reactive({ favicon: null, logo: null, faviconName: '', logoName: '' });
const logKeySet = new Set();
const pendingCredentialSync = new Map();
const activeCredentialSync = new Set();
const completedCredentialSync = new Set();
const conceptProjects = ref([]);
const conceptProjectsLoading = ref(false);
const conceptProjectsError = ref('');
const selectedConceptProjectId = ref('');
const applyingConceptProject = ref(false);
const conceptAssetPreview = ref({ ...EMPTY_CONCEPT_ASSETS });
const selectedFileSourceMode = ref(FILE_SOURCE_MANUAL);
const manualFaviconInputRef = ref(null);
const manualLogoInputRef = ref(null);
const isAddingCategory = ref(false);
const editingCategoryIndex = ref(-1);
const categoryDraft = ref(createEmptyCategory());
const categoryEditDraft = ref(createEmptyCategory());

const form = reactive({ ...FORM_DEFAULTS, categories: [] });

const adminFields = [
  { key: 'adminUser', label: 'Логин администратора *', type: 'text', wrapperClass: 'col-md-4' },
  { key: 'adminEmail', label: 'Email администратора *', type: 'email', wrapperClass: 'col-md-4' },
  { key: 'adminPassword', label: 'Пароль администратора *', type: 'password', wrapperClass: 'col-md-4' },
];
const dbFields = [
  { key: 'dbHost', label: 'Хост DB *', type: 'text', wrapperClass: 'col-md-3' }, { key: 'dbName', label: 'Имя DB *', type: 'text', wrapperClass: 'col-md-3' },
  { key: 'dbUser', label: 'Пользователь DB *', type: 'text', wrapperClass: 'col-md-3' }, { key: 'dbPassword', label: 'Пароль DB *', type: 'password', wrapperClass: 'col-md-3' },
];
const optionalFields = [
  { key: 'loginSlug', label: 'Login slug', type: 'text', wrapperClass: 'col-md-6' },
];

const normalizeString = value => String(value || '').trim();
const upper = value => String(value || '').toUpperCase();
const extractError = error => error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Неизвестная ошибка';
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
const toPlainObject = value => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
const toNullableString = value => {
  const normalized = normalizeString(value);
  return normalized || null;
};
const normalizeCategoryObject = item => {
  if (item && typeof item === 'object') {
    return {
      name: normalizeString(item.name || item.categoryName || item.title),
      slug: normalizeString(item.slug),
      description: normalizeString(item.description),
    };
  }
  const raw = normalizeString(item);
  if (!raw) return createEmptyCategory();
  const parts = raw.split('|').map(x => normalizeString(x));
  return {
    name: parts[0] || '',
    slug: parts[1] || '',
    description: parts.length > 2 ? parts.slice(2).join('|') : '',
  };
};
const normalizeCategoriesForForm = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => normalizeCategoryObject(item))
      .filter(item => item.name || item.slug || item.description);
  }

  const raw = normalizeString(value);
  if (!raw) return [];
  const rows = raw.includes('\n')
    ? raw.split(/\r?\n/g)
    : raw.split(',');
  return rows
    .map(row => normalizeCategoryObject(row))
    .filter(item => item.name || item.slug || item.description);
};
const createCategoryDraft = category => ({
  name: normalizeString(category?.name),
  slug: normalizeString(category?.slug),
  description: normalizeString(category?.description),
});
const normalizeConceptProject = payload => {
  const source = toPlainObject(payload);
  const domainsSource = Array.isArray(source.domains) ? source.domains : [];
  const domains = domainsSource.map(item => {
    const domainSource = toPlainObject(item);
    return {
      id: toNullableString(domainSource.id),
      domainName: normalizeString(domainSource.domainName || domainSource.domain_name),
    };
  });
  const selectedDomainId = toNullableString(source.selectedDomainId || source.selected_domain_id);
  const selectedDomain = domains.find(item => normalizeString(item.id) === normalizeString(selectedDomainId)) || null;
  const directSelectedDomainName = normalizeString(
    source.selectedDomainName
    || source.selected_domain_name
    || toPlainObject(source.selectedDomain).domainName
    || toPlainObject(source.selected_domain).domain_name
    || source.domain
  );
  const languageCode = normalizeString(source.languageCode || source.language_code || source.selectedLocalLanguageCode || source.selected_local_language_code).toLowerCase();
  const sourceLocale = normalizeString(source.sourceLocale || source.source_locale).toLowerCase();
  return {
    id: toNullableString(source.id),
    topicName: normalizeString(source.topicName || source.topic_name),
    selectedDomainName: normalizeString(directSelectedDomainName || selectedDomain?.domainName || domains[0]?.domainName),
    languageCode: languageCode || sourceLocale || 'ru',
    sourceLocale: sourceLocale || languageCode || 'ru',
  };
};
const resolveLocalizationDataPayload = payload => {
  const source = toPlainObject(payload);
  if (source.localizationData && typeof source.localizationData === 'object') return source.localizationData;
  if (source.localization_data && typeof source.localization_data === 'object') return source.localization_data;
  if (source.concept || source.identity) {
    return {
      selected_concept: source.concept,
      site_identity: source.identity,
    };
  }
  return source;
};
const resolveWpLocaleFromLanguage = languageCode => {
  const normalized = normalizeString(languageCode).toLowerCase();
  if (!normalized) return FORM_DEFAULTS.locale;
  if (normalized === 'ru') return 'ru_RU';
  if (normalized === 'en') return 'en_US';
  if (normalized === 'es') return 'es_ES';
  const byPrefix = localeOptions.find(option => normalizeString(option.value).toLowerCase().startsWith(`${normalized}_`));
  return byPrefix?.value || FORM_DEFAULTS.locale;
};
const extractIdentityFromResolvedContent = payload => {
  const source = toPlainObject(resolveLocalizationDataPayload(payload));
  const identity = toPlainObject(source.site_identity || source.siteIdentity || source.identity);
  return {
    siteTitle: normalizeString(identity.siteTitle || identity.site_title),
    siteTagline: normalizeString(identity.siteTagline || identity.site_tagline),
    homeDescription: normalizeString(identity.homeDescription || identity.home_description),
    categories: normalizeCategoriesForForm(identity.categories),
  };
};
const extractDesignFromResolvedContent = payload => {
  const design = toPlainObject(toPlainObject(payload).design);
  const result = {};
  // LLM-generated parts are already in the wpboot contract shape; pass them through.
  if (design.palette && typeof design.palette === 'object') result.palette = design.palette;
  if (design.typography && typeof design.typography === 'object') result.typography = design.typography;
  if (design.texts && typeof design.texts === 'object') result.texts = design.texts;
  if (design.privacyPolicy && typeof design.privacyPolicy === 'object') result.privacyPolicy = design.privacyPolicy;
  // Brand tokens derived from the logo (fallback input for wpboot).
  const brand = toPlainObject(design.brandTokens);
  const palette = Array.isArray(brand.logoPalette) ? brand.logoPalette.filter(Boolean) : [];
  const logoTextColor = normalizeString(brand.logoTextColor);
  const logoFontKey = normalizeString(brand.logoFontKey);
  if (palette.length || logoTextColor || logoFontKey) {
    result.brandTokens = {
      ...(palette.length ? { logoPalette: palette } : {}),
      ...(logoTextColor ? { logoTextColor } : {}),
      ...(logoFontKey ? { logoFontKey } : {}),
    };
  }
  return Object.keys(result).length ? result : null;
};
// Build the design sent to wpboot. The saved SiteDesign payload (GET /design) is the source
// of truth for the generated parts (palette/typography/texts/privacyPolicy); brand tokens come
// from resolved-content (the logo). This avoids depending on resolved-content exposing every field.
const buildDeployDesign = (designPayload, resolvedPayload) => {
  const result = extractDesignFromResolvedContent(resolvedPayload) || {};
  const payload = toPlainObject(toPlainObject(designPayload).payload);
  if (payload.palette && typeof payload.palette === 'object') result.palette = payload.palette;
  if (payload.typography && typeof payload.typography === 'object') result.typography = payload.typography;
  if (payload.texts && typeof payload.texts === 'object') result.texts = payload.texts;
  if (payload.privacyPolicy && typeof payload.privacyPolicy === 'object') result.privacyPolicy = payload.privacyPolicy;
  return Object.keys(result).length ? result : null;
};
const resolveImageUrlFromPayload = payload => {
  const source = toPlainObject(payload);
  return normalizeString(
    source.selectedPublicUrl
    || source.selected_public_url
    || source.publicUrl
    || source.public_url
    || source.croppedPublicUrl
    || source.cropped_public_url
  );
};
const extensionFromMime = mime => {
  const value = normalizeString(mime).toLowerCase();
  if (!value) return '';
  if (value.includes('png')) return '.png';
  if (value.includes('svg')) return '.svg';
  if (value.includes('jpeg') || value.includes('jpg')) return '.jpg';
  if (value.includes('x-icon') || value.includes('ico') || value.includes('icon')) return '.ico';
  if (value.includes('webp')) return '.webp';
  return '';
};
const extensionFromUrl = url => {
  const value = normalizeString(url);
  if (!value) return '';
  const clean = value.split('?')[0].split('#')[0];
  const match = clean.match(/(\.[a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : '';
};
const buildConceptFileName = (baseName, url, mime) => {
  const extension = extensionFromUrl(url) || extensionFromMime(mime) || '.png';
  return `${baseName}${extension}`;
};
const mapFileLoadError = error => {
  const message = normalizeString(error?.message || extractError(error));
  if (!message) return 'не удалось получить файл';
  if (message === 'Failed to fetch') {
    return 'браузер заблокировал чтение файла (CORS на storage URL)';
  }
  return message;
};
const fetchFileFromUrl = async (url, fileName) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Не удалось загрузить файл (${response.status}).`);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
};
let infoMessageTimer = null;
const clearInfoMessage = () => {
  if (infoMessageTimer) {
    clearTimeout(infoMessageTimer);
    infoMessageTimer = null;
  }
  infoMessage.value = '';
};
const showInfoMessage = (message, timeoutMs = INFO_MESSAGE_TIMEOUT_MS) => {
  const text = normalizeString(message);
  clearInfoMessage();
  if (!text) return;
  infoMessage.value = text;
  if (Number(timeoutMs) > 0) {
    infoMessageTimer = setTimeout(() => {
      infoMessage.value = '';
      infoMessageTimer = null;
    }, timeoutMs);
  }
};
const selectedHistoryItem = computed(() => historyItems.value.find(x => normalizeString(x?.deploymentId) === deploymentId.value) || null);
const statusValue = computed(() => deploymentStatus.value?.status || selectedHistoryItem.value?.status || '');
const detailsErrorMessage = computed(() => deploymentStatus.value?.errorMessage || selectedHistoryItem.value?.errorMessage || '');
const progressValue = computed(() => {
  const raw = deploymentStatus.value?.progressPercent ?? selectedHistoryItem.value?.progressPercent;
  const num = Number(raw);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, Math.round(num)));
});
const isRunningDeployment = computed(() => isRunningStatus(statusValue.value));
const filteredLogs = computed(() => (logFilter.value === 'all' ? logs.value : logs.value.filter(x => x.kind === logFilter.value)));
const historyPages = computed(() => Array.from({ length: historyTotalPages.value }, (_, index) => index + 1));
const showDeleteDeploymentModal = computed(() => Boolean(deploymentDeleteCandidate.value?.id));
const modeQuery = computed(() => normalizeString(route.query.mode));
const isDetailsMode = computed(() => Boolean(deploymentId.value));
const isCreateMode = computed(() => !deploymentId.value && modeQuery.value === CREATE_MODE_QUERY);
const isListMode = computed(() => !deploymentId.value && !isCreateMode.value);
const isFormMode = computed(() => isCreateMode.value || isDetailsMode.value);
const selectedConceptProjectSummary = computed(
  () => conceptProjects.value.find(item => normalizeString(item.id) === normalizeString(selectedConceptProjectId.value)) || null
);
const hasProjectAssetPreview = computed(
  () => Boolean(normalizeString(conceptAssetPreview.value.faviconUrl) && normalizeString(conceptAssetPreview.value.logoUrl))
);
const isProjectFileMode = computed(() => selectedFileSourceMode.value === FILE_SOURCE_PROJECT);
const { lastError: sseError, open: openEvents, close: closeEvents } = useDeploymentEvents();

const clearErrors = () => Object.keys(fieldErrors).forEach(key => delete fieldErrors[key]);
const setFormFrom = payload => {
  Object.keys(FORM_DEFAULTS).forEach(key => {
    const value = payload?.[key];
    if (value === null || value === undefined || value === '') {
      if (key === 'categories') {
        form[key] = [];
        return;
      }
      form[key] = FORM_DEFAULTS[key];
      return;
    }
    if (key === 'sshPort') {
      form[key] = Number(value) || FORM_DEFAULTS.sshPort;
      return;
    }
    if (key === 'categories') {
      form[key] = normalizeCategoriesForForm(value);
      return;
    }
    form[key] = value;
  });
  if (payload && typeof payload === 'object' && !Object.prototype.hasOwnProperty.call(payload, 'useTurnstile')) {
    form.useTurnstile = Boolean(normalizeString(payload.turnstileSiteKey) || normalizeString(payload.turnstileSecretKey));
  }
  sshAuthMode.value = normalizeString(form.sshPrivateKey) ? 'key' : 'password';
  useSshPassphrase.value = Boolean(normalizeString(form.sshPrivateKeyPassphrase));
  useSshPort.value = Number(form.sshPort) > 0 && Number(form.sshPort) !== 22;
  isAddingCategory.value = false;
  editingCategoryIndex.value = -1;
  categoryDraft.value = createEmptyCategory();
  categoryEditDraft.value = createEmptyCategory();
};
const isEditingCategory = index => Number(index) === Number(editingCategoryIndex.value);
const startAddCategory = () => {
  isAddingCategory.value = true;
  editingCategoryIndex.value = -1;
  categoryDraft.value = createEmptyCategory();
  delete fieldErrors.categories;
};
const cancelAddCategory = () => {
  isAddingCategory.value = false;
  categoryDraft.value = createEmptyCategory();
};
const addCategoryRow = () => {
  const next = createCategoryDraft(categoryDraft.value);
  if (!next.name && !next.slug && !next.description) {
    uiError.value = 'Заполните хотя бы название категории.';
    return;
  }
  if (!next.name && (next.slug || next.description)) {
    uiError.value = 'У категории обязательно поле «Название».';
    return;
  }
  if (!Array.isArray(form.categories)) form.categories = [];
  form.categories.push(next);
  isAddingCategory.value = false;
  categoryDraft.value = createEmptyCategory();
  delete fieldErrors.categories;
};
const startEditCategory = index => {
  if (!Array.isArray(form.categories)) return;
  const category = form.categories[index];
  if (!category) return;
  editingCategoryIndex.value = index;
  categoryEditDraft.value = createCategoryDraft(category);
  isAddingCategory.value = false;
  delete fieldErrors.categories;
};
const cancelEditCategory = () => {
  editingCategoryIndex.value = -1;
  categoryEditDraft.value = createEmptyCategory();
};
const saveCategoryEdits = index => {
  if (!isEditingCategory(index) || !Array.isArray(form.categories)) return;
  const next = createCategoryDraft(categoryEditDraft.value);
  if (!next.name && !next.slug && !next.description) {
    form.categories.splice(index, 1);
    cancelEditCategory();
    delete fieldErrors.categories;
    return;
  }
  if (!next.name && (next.slug || next.description)) {
    uiError.value = 'У категории обязательно поле «Название».';
    return;
  }
  form.categories.splice(index, 1, next);
  cancelEditCategory();
  delete fieldErrors.categories;
};
const removeCategoryRow = index => {
  if (!Array.isArray(form.categories)) return;
  if (index < 0 || index >= form.categories.length) return;
  form.categories.splice(index, 1);
  if (editingCategoryIndex.value === index) {
    cancelEditCategory();
  } else if (editingCategoryIndex.value > index) {
    editingCategoryIndex.value -= 1;
  }
  delete fieldErrors.categories;
};
const clearSelectedFiles = () => {
  fileState.favicon = null;
  fileState.logo = null;
  fileState.faviconName = '';
  fileState.logoName = '';
};
const loadFilesFromProjectAssets = async (options = {}) => {
  const logoUrl = normalizeString(conceptAssetPreview.value.logoUrl);
  const faviconUrl = normalizeString(conceptAssetPreview.value.faviconUrl);
  if (!logoUrl || !faviconUrl) {
    throw new Error('В выбранном проекте нет полного набора logo/favicon.');
  }

  const fileLoadErrors = [];
  try {
    fileState.logo = await fetchFileFromUrl(logoUrl, buildConceptFileName('logo-from-concept', logoUrl, ''));
    fileState.logoName = fileState.logo.name;
    delete fieldErrors.logo;
  } catch (error) {
    fileLoadErrors.push(`logo: ${mapFileLoadError(error)}`);
  }
  try {
    fileState.favicon = await fetchFileFromUrl(faviconUrl, buildConceptFileName('favicon-from-concept', faviconUrl, ''));
    fileState.faviconName = fileState.favicon.name;
    delete fieldErrors.favicon;
  } catch (error) {
    fileLoadErrors.push(`favicon: ${mapFileLoadError(error)}`);
  }

  if (fileLoadErrors.length) {
    if (options?.silent !== true) {
      showInfoMessage(`Файлы проекта загрузить не удалось: ${fileLoadErrors.join('; ')}`, CREDENTIAL_SYNC_INFO_TIMEOUT_MS);
    }
    throw new Error(fileLoadErrors.join('; '));
  }
};
const switchFileSourceMode = async mode => {
  const normalizedMode = normalizeString(mode).toLowerCase();
  if (normalizedMode !== FILE_SOURCE_PROJECT && normalizedMode !== FILE_SOURCE_MANUAL) return;
  if (normalizedMode === selectedFileSourceMode.value) return;

  if (normalizedMode === FILE_SOURCE_MANUAL) {
    selectedFileSourceMode.value = FILE_SOURCE_MANUAL;
    clearSelectedFiles();
    delete fieldErrors.favicon;
    delete fieldErrors.logo;
    return;
  }

  if (!hasProjectAssetPreview.value) {
    uiError.value = 'Сначала примените проект-концепцию с logo и favicon.';
    return;
  }

  try {
    await loadFilesFromProjectAssets({ silent: false });
    selectedFileSourceMode.value = FILE_SOURCE_PROJECT;
  } catch {
    selectedFileSourceMode.value = FILE_SOURCE_MANUAL;
  }
};
const openManualFilePicker = field => {
  if (field === 'favicon') {
    manualFaviconInputRef.value?.click();
    return;
  }
  manualLogoInputRef.value?.click();
};
const resetFormState = () => {
  setFormFrom(FORM_DEFAULTS);
  createOrUpdateCredential.value = true;
  selectedFileSourceMode.value = FILE_SOURCE_MANUAL;
  clearSelectedFiles();
  conceptAssetPreview.value = { ...EMPTY_CONCEPT_ASSETS };
  isAddingCategory.value = false;
  editingCategoryIndex.value = -1;
  categoryDraft.value = createEmptyCategory();
  categoryEditDraft.value = createEmptyCategory();
  clearErrors();
  submitError.value = '';
  uiError.value = '';
};
const loadConceptProjects = async () => {
  conceptProjectsLoading.value = true;
  conceptProjectsError.value = '';
  try {
    const list = await getSiteProjects();
    const enriched = await Promise.all(list.map(async item => {
      const normalized = normalizeConceptProject(item);
      const projectId = normalizeString(normalized.id);
      if (!projectId) return normalized;
      if (normalized.selectedDomainName) return normalized;
      try {
        const detailed = normalizeConceptProject(await getSiteProject(projectId));
        return {
          ...normalized,
          selectedDomainName: detailed.selectedDomainName || normalized.selectedDomainName,
        };
      } catch {
        return normalized;
      }
    }));
    conceptProjects.value = enriched
      .filter(item => normalizeString(item.id))
      .sort((a, b) => String(a.topicName || '').localeCompare(String(b.topicName || ''), 'ru'));
  } catch (error) {
    conceptProjectsError.value = extractError(error);
    conceptProjects.value = [];
  } finally {
    conceptProjectsLoading.value = false;
  }
};
const applyConceptToForm = async () => {
  const projectId = normalizeString(selectedConceptProjectId.value);
  if (!projectId || applyingConceptProject.value) return;
  applyingConceptProject.value = true;
  uiError.value = '';
  clearInfoMessage();
  conceptProjectsError.value = '';
  try {
    const [projectPayload, resolvedPayload, designPayload, logoPayload, faviconPayload] = await Promise.all([
      getSiteProject(projectId),
      getSiteProjectResolvedContent(projectId).catch(() => null),
      getSiteProjectDesign(projectId).catch(() => null),
      getSiteProjectLogo(projectId).catch(() => null),
      getSiteProjectFavicon(projectId).catch(() => null),
    ]);

    const project = normalizeConceptProject(projectPayload);
    const identity = extractIdentityFromResolvedContent(resolvedPayload || {});
    form.design = buildDeployDesign(designPayload, resolvedPayload || {});
    const nextDomain = normalizeString(project.selectedDomainName);
    const nextTitle = normalizeString(identity.siteTitle);
    const nextTagline = normalizeString(identity.siteTagline);
    const nextDescription = normalizeString(identity.homeDescription);
    const nextCategories = normalizeCategoriesForForm(identity.categories);
    const nextLocale = resolveWpLocaleFromLanguage(project.languageCode || project.sourceLocale);

    if (nextDomain) {
      form.domain = nextDomain;
      form.sshHost = nextDomain;
    }
    form.siteTitle = nextTitle;
    form.blogDescription = nextTagline;
    form.siteDescription = nextDescription;
    form.locale = nextLocale;
    form.categories = nextCategories;
    isAddingCategory.value = false;
    editingCategoryIndex.value = -1;
    categoryDraft.value = createEmptyCategory();
    categoryEditDraft.value = createEmptyCategory();
    delete fieldErrors.domain;
    delete fieldErrors.sshHost;
    delete fieldErrors.siteTitle;
    delete fieldErrors.blogDescription;
    delete fieldErrors.siteDescription;
    delete fieldErrors.categories;

    const logoUrl = resolveImageUrlFromPayload(logoPayload);
    const faviconUrl = resolveImageUrlFromPayload(faviconPayload);
    conceptAssetPreview.value = { logoUrl, faviconUrl };
    if (logoUrl && faviconUrl) {
      try {
        await loadFilesFromProjectAssets({ silent: true });
        selectedFileSourceMode.value = FILE_SOURCE_PROJECT;
        showInfoMessage('Данные проекта-концепции применены. Используются файлы проекта.');
      } catch (error) {
        selectedFileSourceMode.value = FILE_SOURCE_MANUAL;
        showInfoMessage(`Данные концепции применены, но файлы проекта не загружены: ${extractError(error)}. Выберите файлы вручную.`, CREDENTIAL_SYNC_INFO_TIMEOUT_MS);
      }
    } else {
      selectedFileSourceMode.value = FILE_SOURCE_MANUAL;
      showInfoMessage('Данные проекта-концепции применены. Для деплоя выберите файлы вручную.');
    }
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    applyingConceptProject.value = false;
  }
};
const formatDate = value => { const d = new Date(value); return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString(); };
const statusClass = status => {
  const s = upper(status);
  if (s === 'SUCCESS') return 'bg-success-subtle text-success border border-success-subtle';
  if (s === 'FAILED') return 'bg-danger-subtle text-danger border border-danger-subtle';
  if (s === 'RUNNING' || s === 'CREATED') return 'bg-primary-subtle text-primary border border-primary-subtle';
  return 'bg-secondary-subtle text-secondary border border-secondary-subtle';
};
const isCurrentDeployment = id => normalizeString(id) === deploymentId.value;
const isDeletingDeployment = id => normalizeString(id) === deletingDeploymentId.value;
const canDeleteDeployment = status => isTerminalStatus(status);

const validate = () => {
  clearErrors();
  REQUIRED_FIELDS.forEach(key => { if (!normalizeString(form[key])) fieldErrors[key] = 'Обязательное поле'; });
  if (sshAuthMode.value === 'key') {
    if (!normalizeString(form.sshPrivateKey)) fieldErrors.sshPrivateKey = 'Обязательное поле';
  } else if (!normalizeString(form.sshPassword)) {
    fieldErrors.sshPassword = 'Обязательное поле';
  }
  if (!Number.isFinite(Number(form.sshPort)) || Number(form.sshPort) <= 0) form.sshPort = 22;
  const categories = Array.isArray(form.categories) ? form.categories : [];
  const hasInvalidCategory = categories.some(item => {
    const name = normalizeString(item?.name);
    const slug = normalizeString(item?.slug);
    const description = normalizeString(item?.description);
    return !name && (slug || description);
  });
  if (hasInvalidCategory) fieldErrors.categories = 'У категории обязательно поле «Название»';
  if (form.useTurnstile) {
    if (!normalizeString(form.turnstileSiteKey)) fieldErrors.turnstileSiteKey = 'Обязательное поле';
    if (!normalizeString(form.turnstileSecretKey)) fieldErrors.turnstileSecretKey = 'Обязательное поле';
  }
  if (!EMAIL_RE.test(normalizeString(form.adminEmail))) fieldErrors.adminEmail = 'Некорректный email';
  if (normalizeString(form.loginSlug) && !LOGIN_SLUG_RE.test(normalizeString(form.loginSlug))) fieldErrors.loginSlug = 'Используйте только буквы, цифры, _ и -';
  if (!normalizeString(form.loginSlug)) form.loginSlug = FORM_DEFAULTS.loginSlug;
  if (!localeOptions.some(option => option.value === normalizeString(form.locale))) form.locale = 'ru_RU';
  if (!normalizeString(form.permalinkStructure)) form.permalinkStructure = FORM_DEFAULTS.permalinkStructure;
  if (isProjectFileMode.value && !hasProjectAssetPreview.value) {
    fieldErrors.favicon = 'В проекте нет favicon.';
    fieldErrors.logo = 'В проекте нет logo.';
  }
  if (!fileState.favicon) fieldErrors.favicon = 'Favicon обязателен';
  if (!fileState.logo) fieldErrors.logo = 'Logo обязателен';
  return Object.keys(fieldErrors).length === 0;
};
watch(
  () => form.useTurnstile,
  enabled => {
    if (enabled) return;
    delete fieldErrors.turnstileSiteKey;
    delete fieldErrors.turnstileSecretKey;
  }
);

const setSshAuthMode = mode => {
  const next = mode === 'key' ? 'key' : 'password';
  if (next === sshAuthMode.value) return;
  sshAuthMode.value = next;
  if (next === 'password') {
    form.sshPrivateKey = '';
    form.sshPrivateKeyPassphrase = '';
    useSshPassphrase.value = false;
    delete fieldErrors.sshPrivateKey;
    delete fieldErrors.sshPrivateKeyPassphrase;
  } else {
    form.sshPassword = '';
    delete fieldErrors.sshPassword;
  }
};

watch(useSshPassphrase, enabled => {
  if (enabled) return;
  form.sshPrivateKeyPassphrase = '';
  delete fieldErrors.sshPrivateKeyPassphrase;
});

const onFileChanged = (event, field) => {
  const file = event?.target?.files?.[0] || null;
  if (event?.target) event.target.value = '';
  selectedFileSourceMode.value = FILE_SOURCE_MANUAL;
  if (field === 'favicon') { fileState.favicon = file; fileState.faviconName = file?.name || ''; delete fieldErrors.favicon; return; }
  fileState.logo = file; fileState.logoName = file?.name || ''; delete fieldErrors.logo;
};

const toKind = item => {
  const type = String(item?.type || '').toLowerCase();
  const stream = String(item?.stream || '').toLowerCase();
  if (type === 'step') return 'step';
  if (type === 'warning') return 'warning';
  if (type === 'error') return 'error';
  if (stream === 'stderr') return 'stderr';
  return 'stdout';
};
const addLogEntry = item => {
  const message = normalizeString(item?.message || item?.text || (typeof item === 'string' ? item : ''));
  const step = normalizeString(item?.step);
  const type = normalizeString(item?.type).toLowerCase();
  const timestamp = item?.timestamp || item?.createdAt || new Date().toISOString();
  const baseKey = `${item?.type || ''}|${item?.timestamp || ''}|${item?.stream || ''}|${message}|${step}`;
  const fallbackByType = {
    done: 'Deployment completed',
    completed: 'Deployment completed',
    login_url: 'Login URL received',
    app_password: 'Application password received',
  };
  const logMessage = message || (step ? `Шаг: ${step}` : fallbackByType[type] || '');
  const log = { timestamp, message: logMessage, kind: toKind(item), key: item?.timestamp || item?.createdAt ? baseKey : `${baseKey}|${logs.value.length}` };
  if (!log.message || logKeySet.has(log.key)) return;
  logKeySet.add(log.key);
  logs.value.push(log);
  if (logs.value.length > MAX_LOG_LINES) logs.value.splice(0, logs.value.length - MAX_LOG_LINES).forEach(x => logKeySet.delete(x.key));
};
const clearLogs = () => { logs.value = []; logKeySet.clear(); };
const syncCredentialForDeployment = async deploymentKey => {
  const normalizedDeploymentId = normalizeString(deploymentKey);
  if (!normalizedDeploymentId) return;
  if (!pendingCredentialSync.has(normalizedDeploymentId)) return;
  if (activeCredentialSync.has(normalizedDeploymentId) || completedCredentialSync.has(normalizedDeploymentId)) return;

  const syncRequest = pendingCredentialSync.get(normalizedDeploymentId);
  const domainLabel = normalizeString(syncRequest?.domain || deploymentStatus.value?.domain || form.domain);
  const credentialLogin = normalizeString(syncRequest?.adminUser || deploymentStatus.value?.adminUser || form.adminUser) || 'admin';
  if (!domainLabel) return;

  activeCredentialSync.add(normalizedDeploymentId);
  try {
    let applicationPassword = '';
    for (let attempt = 1; attempt <= APPLICATION_PASSWORD_RETRY_ATTEMPTS; attempt += 1) {
      try {
        const data = await getDeploymentApplicationPassword(normalizedDeploymentId);
        applicationPassword = normalizeString(data?.applicationPassword);
        if (!applicationPassword) throw new Error(`Пустой application password для деплоймента: ${normalizedDeploymentId}`);
        break;
      } catch (error) {
        const status = Number(error?.response?.status);
        const isLastAttempt = attempt >= APPLICATION_PASSWORD_RETRY_ATTEMPTS;
        if (status !== 404 || isLastAttempt) throw error;
        await sleep(APPLICATION_PASSWORD_RETRY_DELAY_MS);
      }
    }

    const result = await upsertWordpressCredentialByLabel({
      label: domainLabel,
      login: credentialLogin,
      password: applicationPassword,
    });

    completedCredentialSync.add(normalizedDeploymentId);
    pendingCredentialSync.delete(normalizedDeploymentId);
    showInfoMessage(
      result?.action === 'updated'
        ? `Credential для ${domainLabel} обновлен`
        : `Credential для ${domainLabel} создан`,
      CREDENTIAL_SYNC_INFO_TIMEOUT_MS
    );
  } catch (error) {
    showInfoMessage(`Deployment завершен, но синхронизация credential не удалась: ${extractError(error)}`, CREDENTIAL_SYNC_INFO_TIMEOUT_MS);
  } finally {
    activeCredentialSync.delete(normalizedDeploymentId);
  }
};

const calculateHistoryPageSize = () => {
  if (typeof window === 'undefined') return HISTORY_DEFAULT_PAGE_SIZE;
  const viewportHeight = Number(window.innerHeight || 0);
  if (!viewportHeight) return HISTORY_DEFAULT_PAGE_SIZE;
  const availableHeight = Math.max(0, viewportHeight - HISTORY_SCREEN_RESERVED_HEIGHT);
  return Math.max(1, Math.min(100, Math.floor(availableHeight / HISTORY_ROW_APPROX_HEIGHT) + 1));
};
const normalizePageSize = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return HISTORY_DEFAULT_PAGE_SIZE;
  return Math.min(100, Math.max(1, Math.round(num)));
};
const normalizePageNumber = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 1;
  return Math.max(1, Math.round(num));
};
const loadHistory = async (options = {}) => {
  const nextPage = normalizePageNumber(options.page ?? historyPage.value);
  const nextSize = normalizePageSize(options.size ?? historySize.value);

  historyLoading.value = true;
  historyError.value = '';
  try {
    const data = await getDeploymentsHistory({ page: nextPage - 1, size: nextSize });
    historyItems.value = Array.isArray(data?.items) ? data.items : [];

    const totalItemsRaw = Number(data?.totalItems);
    historyTotalItems.value = Number.isFinite(totalItemsRaw) && totalItemsRaw >= 0 ? Math.round(totalItemsRaw) : historyItems.value.length;

    const totalPagesRaw = Number(data?.totalPages);
    const totalPagesByItems = historyTotalItems.value > 0 ? Math.ceil(historyTotalItems.value / Math.max(1, nextSize)) : 0;
    historyTotalPages.value = Number.isFinite(totalPagesRaw) && totalPagesRaw >= 0 ? Math.round(totalPagesRaw) : totalPagesByItems;

    const responsePageRaw = Number(data?.page);
    const responsePage = Number.isFinite(responsePageRaw) ? responsePageRaw + 1 : nextPage;
    historyPage.value = historyTotalPages.value > 0 ? Math.min(Math.max(1, responsePage), historyTotalPages.value) : 1;
    historySize.value = normalizePageSize(data?.size ?? nextSize);
  } catch (error) {
    historyError.value = extractError(error);
  } finally {
    historyLoading.value = false;
  }
};
const goToHistoryPage = async page => {
  if (!historyTotalPages.value) return;
  const nextPage = Math.min(historyTotalPages.value, Math.max(1, normalizePageNumber(page)));
  if (nextPage === historyPage.value && historyItems.value.length) return;
  await loadHistory({ page: nextPage });
};
const syncHistoryPageSize = async () => {
  const nextSize = normalizePageSize(calculateHistoryPageSize());
  if (nextSize === historySize.value) return;
  historySize.value = nextSize;
  if (!isListMode.value) return;
  await loadHistory({ page: historyPage.value, size: nextSize });
};
const refreshDeployment = async id => {
  const details = await getDeploymentStatus(id);
  deploymentStatus.value = details?.status ? { ...details, status: upper(details.status) } : details;
  setFormFrom(details || FORM_DEFAULTS);
  const logsData = await getDeploymentLogs(id);
  clearLogs();
  const list = Array.isArray(logsData) ? logsData : logsData?.logs || logsData?.items || [];
  list.forEach(item => addLogEntry(typeof item === 'string' ? { type: 'log', message: item } : item));
};
const connectEventsIfNeeded = () => {
  if (!deploymentId.value || !isRunningStatus(deploymentStatus.value?.status)) { closeEvents(); return; }
  openEvents(deploymentId.value, {
    onNamedEvent: async (eventType, payload) => {
      const normalizedEventType = normalizeString(eventType).toLowerCase();
      if (payload && typeof payload === 'object') {
        const nextStatus = { ...(deploymentStatus.value || {}) };
        if (payload.deploymentId) nextStatus.deploymentId = payload.deploymentId;
        if (payload.status) nextStatus.status = upper(payload.status);
        if (payload.currentStep) nextStatus.currentStep = payload.currentStep;
        if (payload.step) nextStatus.currentStep = payload.step;
        if (Number.isFinite(Number(payload.progressPercent))) nextStatus.progressPercent = Number(payload.progressPercent);
        else if (Number.isFinite(Number(payload.percent))) nextStatus.progressPercent = Number(payload.percent);
        if (payload.siteUrl) nextStatus.siteUrl = payload.siteUrl;
        if (payload.loginUrl) nextStatus.loginUrl = payload.loginUrl;
        if (payload.applicationPassword) nextStatus.applicationPassword = payload.applicationPassword;
        if (payload.appPassword) nextStatus.applicationPassword = payload.appPassword;
        if (normalizedEventType === 'login_url' && normalizeString(payload.message)) nextStatus.loginUrl = normalizeString(payload.message);
        if (normalizedEventType === 'app_password' && normalizeString(payload.message)) nextStatus.applicationPassword = normalizeString(payload.message);
        if (payload.adminUser) nextStatus.adminUser = payload.adminUser;
        if (payload.createdAt) nextStatus.createdAt = payload.createdAt;
        if (payload.startedAt) nextStatus.startedAt = payload.startedAt;
        if (payload.finishedAt) nextStatus.finishedAt = payload.finishedAt;
        if (payload.errorMessage) nextStatus.errorMessage = payload.errorMessage;
        if (payload.error) nextStatus.errorMessage = payload.error;
        deploymentStatus.value = nextStatus;
      }
      if (['started', 'step', 'progress', 'log', 'warning', 'error', 'completed', 'done', 'login_url', 'app_password'].includes(normalizedEventType)) {
        addLogEntry({ ...payload, type: normalizedEventType });
      }
      if (normalizedEventType === 'done' || normalizedEventType === 'completed') {
        const completedDeploymentId = deploymentId.value;
        await refreshDeployment(completedDeploymentId);
        if (upper(deploymentStatus.value?.status) === 'SUCCESS') await syncCredentialForDeployment(completedDeploymentId);
        closeEvents();
      }
    },
  });
};

const loadDeployment = async id => {
  deploymentId.value = id;
  createOrUpdateCredential.value = true;
  uiError.value = '';
  clearInfoMessage();
  try {
    await refreshDeployment(id);
    if (upper(deploymentStatus.value?.status) === 'SUCCESS') await syncCredentialForDeployment(id);
    connectEventsIfNeeded();
  } catch (error) {
    uiError.value = Number(error?.response?.status) === 404 ? `Деплоймент ${id} не найден` : extractError(error);
  }
};
const openCreateMode = async () => {
  closeEvents();
  deploymentId.value = '';
  deploymentStatus.value = null;
  clearLogs();
  resetFormState();
  await router.push({ path: '/tools/wordpress-deployment', query: { mode: CREATE_MODE_QUERY } });
};
const openListMode = async () => {
  closeEvents();
  deploymentId.value = '';
  deploymentStatus.value = null;
  deploymentDeleteCandidate.value = null;
  clearLogs();
  await router.push({ path: '/tools/wordpress-deployment' });
};
const openDeployment = async id => {
  const normalizedId = normalizeString(id);
  if (!normalizedId) return;
  submitError.value = '';
  uiError.value = '';
  if (deploymentId.value === normalizedId) { await loadDeployment(normalizedId); return; }
  await router.push({ name: 'WordPressDeployment', params: { deploymentId: normalizedId }, query: {} });
};
const requestDeleteDeployment = item => {
  const id = normalizeString(item?.deploymentId);
  if (!id || isDeletingDeployment(id)) return;
  uiError.value = '';
  clearInfoMessage();

  if (!canDeleteDeployment(item?.status)) {
    uiError.value = `Удаление доступно только для SUCCESS/FAILED: ${id}`;
    return;
  }
  deploymentDeleteCandidate.value = {
    id,
    status: normalizeString(item?.status),
  };
};

const closeDeleteDeploymentModal = () => {
  if (deletingDeploymentId.value) return;
  deploymentDeleteCandidate.value = null;
};

const confirmDeleteDeployment = async () => {
  const id = normalizeString(deploymentDeleteCandidate.value?.id);
  if (!id || isDeletingDeployment(id)) return;
  deletingDeploymentId.value = id;
  try {
    await deleteWpDeployment(id);
    deploymentDeleteCandidate.value = null;
    showInfoMessage(`Деплоймент ${id} удален`);
    const shouldMovePrevPage = historyItems.value.length === 1 && historyPage.value > 1;
    await loadHistory({ page: shouldMovePrevPage ? historyPage.value - 1 : historyPage.value });
  } catch (error) {
    const status = Number(error?.response?.status);
    uiError.value = status === 401 ? 'Нет авторизации. Войдите снова.' : extractError(error);
    if (status === 400 || status === 404) await loadHistory({ page: historyPage.value });
  } finally {
    deletingDeploymentId.value = '';
  }
};
const submitDeployment = async () => {
  uiError.value = '';
  clearInfoMessage();
  submitError.value = '';
  if (!validate()) return;
  const shouldSyncCredential = createOrUpdateCredential.value;
  const domainLabel = normalizeString(form.domain);
  submitting.value = true;
  try {
    const formData = buildCreateDeploymentFormData({ ...form, design: form.design, favicon: fileState.favicon, logo: fileState.logo });
    const data = await createDeployment(formData);
    const id = normalizeString(data?.deploymentId);
    if (!id) throw new Error('Бэкенд не вернул deploymentId');
    if (shouldSyncCredential && domainLabel) {
      pendingCredentialSync.set(id, { domain: domainLabel, adminUser: normalizeString(form.adminUser) || 'admin' });
      completedCredentialSync.delete(id);
    }
    await router.push({ name: 'WordPressDeployment', params: { deploymentId: id }, query: {} });
    await loadHistory();
  } catch (error) {
    submitError.value = extractError(error);
  } finally {
    submitting.value = false;
  }
};
const copyValue = async value => {
  try {
    await navigator.clipboard.writeText(String(value || ''));
    showInfoMessage('Скопировано в буфер обмена');
  } catch {
    showInfoMessage('Не удалось скопировать');
  }
};
const copyLogs = async () => copyValue(filteredLogs.value.map(x => `[${new Date(x.timestamp).toLocaleTimeString()}] ${x.kind.toUpperCase()} ${x.message}`).join('\n'));
const downloadLogs = () => {
  const text = filteredLogs.value.map(x => `[${new Date(x.timestamp).toLocaleTimeString()}] ${x.kind.toUpperCase()} ${x.message}`).join('\n');
  const href = URL.createObjectURL(new Blob([text || ''], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = href;
  link.download = `wp-deployment-${deploymentId.value || 'logs'}.txt`;
  link.click();
  URL.revokeObjectURL(href);
};

watch(
  () => [route.params.deploymentId, normalizeString(route.query.mode)],
  ([routeDeploymentId, routeMode], previous = []) => {
    const prevMode = previous[1];
    const id = normalizeString(routeDeploymentId);
    if (!id) {
      closeEvents();
      deploymentId.value = '';
      deploymentStatus.value = null;
      deploymentDeleteCandidate.value = null;
      clearLogs();
      if (routeMode === CREATE_MODE_QUERY && prevMode !== CREATE_MODE_QUERY) resetFormState();
      if (routeMode !== CREATE_MODE_QUERY) loadHistory();
      return;
    }
    if (!historyItems.value.length) loadHistory();
    loadDeployment(id);
  },
  { immediate: true }
);
watch(
  () => isCreateMode.value,
  nextIsCreateMode => {
    if (!nextIsCreateMode) return;
    if (conceptProjects.value.length || conceptProjectsLoading.value) return;
    loadConceptProjects();
  },
  { immediate: true }
);

let historyResizeTimer = null;
const onHistoryResize = () => {
  if (historyResizeTimer) clearTimeout(historyResizeTimer);
  historyResizeTimer = setTimeout(() => {
    syncHistoryPageSize();
  }, 120);
};

onMounted(() => {
  syncHistoryPageSize();
  loadConceptProjects();
  window.addEventListener('resize', onHistoryResize);
});

onBeforeUnmount(() => {
  clearInfoMessage();
  if (historyResizeTimer) clearTimeout(historyResizeTimer);
  window.removeEventListener('resize', onHistoryResize);
});
</script>

<style scoped>
.wp-deploy-page {
  min-height: 100%;
  padding-top: 10px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
}

.wpd-hero {
  margin: 0 20px 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
}

.wpd-hero__title {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.wpd-hero__subtitle {
  color: rgba(232, 242, 255, 0.92);
  font-size: 13px;
}

.wpd-hero__actions {
  flex-wrap: wrap;
}

.tool-notifications {
  position: fixed;
  top: 14px;
  right: 18px;
  z-index: 1100;
  pointer-events: none;
}

.tool-notifications__list {
  display: grid;
  gap: 8px;
}

.tool-notification {
  max-width: min(440px, calc(100vw - 24px));
  border-radius: 10px;
  border: 1px solid;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.35;
  box-shadow: 0 12px 24px rgba(16, 48, 95, 0.18);
  pointer-events: auto;
}

.tool-notification--error {
  border-color: #f3c7ce;
  background: #fff0f2;
  color: #943543;
}

.tool-notification--info {
  border-color: #cfe1fb;
  background: #eef5ff;
  color: #234f87;
}

.tool-toast-enter-active,
.tool-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tool-toast-enter-from,
.tool-toast-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.wpd-hero__action-main {
  color: #103f77;
  border-color: #cfe2ff;
  font-weight: 700;
}

.wpd-surface {
  border: 1px solid #dce7fa !important;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(24, 55, 100, 0.08) !important;
}

.section-title {
  color: #1f2d45;
  font-weight: 700;
}

.history-headline {
  margin-bottom: 2px;
}

.history-list {
  display: grid;
  gap: 8px;
}

.history-item {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 10px 11px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.history-item:hover {
  border-color: #bed5f7;
  box-shadow: 0 8px 18px rgba(24, 55, 100, 0.09);
}

.history-item.active {
  border-color: #b8cdf1;
  box-shadow: 0 0 0 1px #dce7fb inset;
  background: #f7fbff;
}

.history-main {
  min-width: 0;
  display: grid;
  gap: 2px;
}

.history-site {
  color: #2f6be5;
  font-size: 12px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-site:hover {
  text-decoration: underline;
}

.history-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.history-actions .btn {
  font-weight: 600;
}

.history-pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.pagination .page-item:not(.disabled) {
  cursor: pointer;
}

.pagination .page-item.disabled {
  cursor: default;
}

.status-progress {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 180px;
}

.status-progress__bar {
  width: 160px;
  height: 8px;
}

.status-progress__label {
  min-width: 34px;
  color: #566482;
  font-size: 11px;
}

.deployment-credential-check.form-check {
  border: 1px solid #dbe8fb;
  background: #f4f9ff;
  border-radius: 10px;
  padding: 10px 11px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.deployment-credential-check .form-check-input {
  float: none;
  margin: 0;
  margin-top: 0.16rem;
  flex: 0 0 auto;
}

.deployment-credential-check .form-check-label {
  font-size: 13px;
  color: #314568;
  user-select: none;
  margin: 0;
}

.wpd-ssh-auth-switch .form-label {
  font-size: 13px;
  color: #314568;
}

.wpd-ssh-key-field :deep(textarea) {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  white-space: pre;
  overflow-wrap: normal;
}

.form-layout {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.section-block {
  border: 1px solid #e2eaf9;
  border-radius: 11px;
  padding: 10px 11px;
  background: #fbfdff;
}

.section-span-2 {
  grid-column: 1 / -1;
}

.section-head {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  color: #516184;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.section-block :deep(.form-label),
.section-block label.form-label {
  font-size: 12px;
  font-weight: 600;
  color: #3f4f70;
  margin-bottom: 4px;
}

.section-block :deep(.form-control.form-control-sm),
.section-block input.form-control-sm,
.section-block textarea.form-control-sm {
  min-height: 34px;
  font-size: 13px;
  border-color: #dfe5f2;
}

.section-block :deep(.form-select.form-select-sm),
.section-block select.form-select-sm {
  min-height: 34px;
  font-size: 13px;
  border-color: #dfe5f2;
}

.section-block :deep(textarea.form-control.form-control-sm),
.section-block textarea.form-control-sm {
  min-height: 82px;
  line-height: 1.35;
}

.wpd-concept-picker {
  display: grid;
  gap: 8px;
}

.wpc-empty-state {
  border: 1px dashed #d9e6fb;
  border-radius: 10px;
  background: #f8fbff;
  color: #5a6f94;
  font-size: 13px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wpc-empty-state i {
  font-size: 16px;
  color: #4a6ea8;
}

.wpc-identity-categories {
  border-top: 1px solid #e6eefb;
  padding-top: 10px;
}

.wpc-identity-categories__head h6 {
  color: #1f2d45;
  font-weight: 700;
  font-size: 14px;
}

.wpc-identity-categories__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.wpc-identity-categories__list {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.wpc-identity-category {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 8px 9px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.wpc-identity-category__main {
  min-width: 0;
  flex: 1;
  color: #2d4264;
  font-size: 13px;
  display: grid;
  gap: 2px;
}

.wpc-identity-category__slug {
  display: inline-flex;
  width: fit-content;
  border-radius: 999px;
  border: 1px solid #d5e4fa;
  background: #eff5ff;
  color: #345a92;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
}

.wpc-identity-category__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.wpc-edit-btn,
.wpc-delete-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.wpc-edit-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.wpc-edit-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wpc-edit-field > span {
  color: #5f7698;
  font-size: 11px;
  font-weight: 700;
}

.wpc-edit-field--full {
  grid-column: 1 / -1;
}

.wpc-concept-card__edit-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.wpc-identity-categories__form {
  margin-top: 10px;
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 9px;
  display: grid;
  gap: 8px;
}

.wpc-identity-categories__actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.wpd-file-panel {
  display: grid;
  gap: 8px;
}

.wpd-file-inline {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
}

.wpd-file-inline--manual {
  align-items: start;
}

.wpd-file-inline__content {
  min-width: 0;
  justify-self: start;
}

.wpd-file-mode__switch .btn {
  min-width: 88px;
  font-weight: 600;
  padding: 2px 9px;
  font-size: 12px;
  line-height: 1.2;
}

.wpd-file-mode__switch {
  width: fit-content;
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  border: 1px solid #c9d9f2;
  border-radius: 999px;
  background: #f5f9ff;
  justify-self: end;
  align-self: start;
}

.wpd-file-mode__switch .btn {
  border: 1px solid transparent !important;
  border-radius: 999px !important;
  background: transparent;
  color: #3f5f8f;
}

.wpd-file-mode__switch .btn.active {
  border-color: #afc8ee !important;
  background: #fff;
  color: #184b91;
}

.wpd-file-mode__switch .btn:disabled {
  opacity: 0.55;
}

.wpd-manual-file-action {
  margin-top: 0;
  white-space: nowrap;
}

.wpd-manual-file-label {
  display: block;
  margin-bottom: 6px;
}

.wpd-concept-assets {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 10px;
  flex: 1 1 360px;
  overflow-x: auto;
}

.wpd-manual-assets {
  flex: 1 1 460px;
  margin-top: 0 !important;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.wpd-manual-assets > [class*='col-'] {
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 220px;
}

.wpd-concept-assets__item {
  display: grid;
  gap: 6px;
  min-width: 180px;
}

.wpd-concept-assets__label {
  color: #516184;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.wpd-concept-assets__thumb {
  border: 1px solid #dce7fa;
  border-radius: 8px;
  background: #fff;
  object-fit: contain;
}

.wpd-concept-assets__thumb--favicon {
  width: 92px;
  height: 92px;
  padding: 10px;
}

.wpd-concept-assets__thumb--logo {
  max-width: 260px;
  height: 92px;
  padding: 8px 10px;
}

.wpc-identity-category__actions .btn {
  font-weight: 600;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
}

.wpd-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 33, 61, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1040;
}

.wpd-modal {
  width: min(100%, 460px);
  border: 1px solid #dce7fa;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 36px rgba(15, 33, 61, 0.22);
  overflow: hidden;
}

.wpd-modal__head {
  padding: 12px 14px;
  border-bottom: 1px solid #e3ebfb;
  background: #f8fbff;
  color: #1f2d45;
}

.wpd-modal__body {
  padding: 12px 14px;
  color: #2c3e5d;
  font-size: 14px;
  line-height: 1.4;
}

.wpd-modal__actions {
  padding: 10px 14px 12px;
  border-top: 1px solid #e3ebfb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 992px) {
  .wpd-hero {
    margin: 0 12px 12px;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start !important;
  }

  .wpd-hero__actions {
    width: 100%;
  }

  .form-layout {
    grid-template-columns: 1fr;
  }

  .section-span-2 {
    grid-column: auto;
  }

  .history-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .history-actions {
    width: 100%;
    justify-content: space-between;
  }

  .history-pagination-wrap {
    align-items: flex-start;
  }

  .wpc-identity-category {
    flex-direction: column;
  }

  .wpd-file-inline {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .wpd-file-mode__switch {
    justify-self: start;
  }
}
</style>

